import React from 'react';
import {
  DataList,
  Modal,
  ModalControls,
  Heading as LumosHeading,
} from '@loanpal/lumos__core';
import { Grid, View, Heading, Flex } from '@aws-amplify/ui-react';
import { Box } from 'theme-ui';
import { getSharedAppStateEntries } from "../../utils/shared-app-states";
import {
  normalizePaymentsShareMethods,
  paymentsShareMethodsEqual,
} from "./payments-tab";

/** Pull a displayable id from one offer entry (API / form / MultiSelect shapes). */
function offerItemToDisplayId(o) {
  if (o == null || o === '') return '';
  if (typeof o === 'string' || typeof o === 'number') return String(o).trim();
  if (typeof o === 'object') {
    const id = o.id ?? o.offerId ?? o.value ?? o.uuid;
    if (id != null && typeof id !== 'object') return String(id).trim();
    if (typeof o.value === 'object' && o.value != null) {
      const inner = offerItemToDisplayId(o.value);
      if (inner) return inner;
    }
  }
  return '';
}

/** Normalize SharedApp `offers` (AWSJSON) or preview payload JSON string for display. */
function formatSharedAppOffers(offers) {
  if (offers == null || offers === '') return '';
  let parsed = offers;
  if (typeof offers === 'string') {
    try {
      parsed = JSON.parse(offers);
    } catch {
      return offers;
    }
  }
  if (Array.isArray(parsed)) {
    return parsed.map((o) => offerItemToDisplayId(o)).filter(Boolean).join(', ');
  }
  if (parsed && typeof parsed === 'object') {
    const one = offerItemToDisplayId(parsed);
    return one || '';
  }
  return String(parsed);
}

function offersComparableKey(offers) {
  return formatSharedAppOffers(offers)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .sort()
    .join(',');
}

function fmtUseConnectedOrgs(useConnected) {
  return useConnected ? 'Yes' : 'No';
}

function fmtOnOff(value) {
  return value ? 'On' : 'Off';
}

function paymentLinkOptionsItems(shareMethods, highlightDiffAgainst) {
  const sm = normalizePaymentsShareMethods(
    shareMethods?.shareMethods != null
      ? shareMethods
      : { shareMethods }
  );
  const base = highlightDiffAgainst
    ? normalizePaymentsShareMethods(
        highlightDiffAgainst?.shareMethods != null
          ? highlightDiffAgainst
          : { shareMethods: highlightDiffAgainst }
      )
    : null;

  const row = (label, key) => {
    const text = fmtOnOff(sm[key]);
    const changed = base && Boolean(base[key]) !== Boolean(sm[key]);
    return {
      label,
      text: changed ? <span style={styles.update}>{text}</span> : text,
    };
  };

  return [
    row('Online (Pay invoice now)', 'online'),
    row('Email', 'email'),
    row('SMS', 'sms'),
  ];
}

const UpdatePreview = ({
  previewCurrent,
  previewUpdate,
  isOpen,
  updateApp,
  toggleOpen,
  paymentsShareMethods,
  paymentsEnabled = false,
}) => {
  const suCurrent = previewCurrent?.submittingUser ?? {};
  const suUpdate = previewUpdate?.submittingUser ?? {};
  const offersCurrentText = formatSharedAppOffers(previewCurrent?.offers);
  const offersUpdateText = formatSharedAppOffers(previewUpdate?.offers);
  const offersChanged = offersComparableKey(previewCurrent?.offers) !== offersComparableKey(previewUpdate?.offers);
  const statesCurrentText = getSharedAppStateEntries(previewCurrent?.states).join(", ");
  const statesUpdateText = getSharedAppStateEntries(previewUpdate?.states).join(", ");
  const statesChanged =
    getSharedAppStateEntries(previewCurrent?.states).sort().join(",") !==
    getSharedAppStateEntries(previewUpdate?.states).sort().join(",");

  const ucCurrent = !!previewCurrent?.useConnectedOrgs;
  const ucUpdate = !!previewUpdate?.useConnectedOrgs;
  const pocCurrent = previewCurrent?.parentOrganizationClientId ?? '';
  const pocUpdate = previewUpdate?.parentOrganizationClientId ?? '';
  const oidCurrent = previewCurrent?.organizationId ?? '';
  const oidUpdate = previewUpdate?.organizationId ?? '';
  const connectedOrgFlagChanged = ucCurrent !== ucUpdate;
  const parentOrgClientIdChanged = pocCurrent !== pocUpdate;
  const organizationIdChanged = oidCurrent !== oidUpdate;
  const showConnectedOrgIds = ucCurrent || ucUpdate;

  const currentPaymentsShareMethods = normalizePaymentsShareMethods(
    previewCurrent?.payments
  );
  const updatePaymentsShareMethods = normalizePaymentsShareMethods({
    shareMethods: paymentsShareMethods,
  });
  const paymentsChanged = !paymentsShareMethodsEqual(
    currentPaymentsShareMethods,
    updatePaymentsShareMethods
  );
  const showPaymentsSection = paymentsEnabled === true;

  const currentDataItems = previewCurrent ? [{
    label: 'Account Name',
    text: previewCurrent.partnerName
  }, {
    label: 'Channel',
    text: previewCurrent.channel
  }, {
    label: 'Category',
    text: previewCurrent.category
  }, {
    label: 'Offers',
    text: offersCurrentText || '—'
  }, {
    label: 'States',
    text: statesCurrentText || '—'
  }, {
    label: `${previewCurrent.calculatorType === "PA" ? "Payment Amount" : "Loan Amount"}`,
    text: previewCurrent.calculatorType === "PA" ? previewCurrent.paymentAmount : previewCurrent.loanAmount
  }, {
    label: 'Submitting User First Name',
    text: suCurrent.firstName ?? ''
  }, {
    label: 'Submitting User Last Name',
    text: suCurrent.lastName ?? ''
  }, {
    label: 'Submitting User Email',
    text: suCurrent.email ?? ''
  }, {
    label: 'Submitting User Phone',
    text: suCurrent.phone ?? ''
  }, {
    label: 'Use Connected Orgs',
    text: fmtUseConnectedOrgs(ucCurrent)
  }, ...(showConnectedOrgIds ? [{
    label: 'Parent Organization Client Id',
    text: pocCurrent || '—'
  }, {
    label: 'Organization Id',
    text: oidCurrent || '—'
  }] : [])] : [];

  const updateDataItems = previewUpdate ? [{
    label: 'Account Name',
    text: previewUpdate.partnerName
  }, {
    label: 'Channel',
    text: previewUpdate.channel !== previewCurrent?.channel ? <span style={styles.update}>{previewUpdate.channel}</span> : previewUpdate.channel
  }, {
    label: 'Category',
    text: previewUpdate.category !== previewCurrent?.category ? <span style={styles.update}>{previewUpdate.category}</span> : previewUpdate.category
  }, {
    label: 'Offers',
    text: offersChanged ? <span style={styles.update}>{offersUpdateText || '—'}</span> : (offersUpdateText || '—')
  }, {
    label: 'States',
    text: statesChanged ? <span style={styles.update}>{statesUpdateText || '—'}</span> : (statesUpdateText || '—')
  }, {
    label: `${previewUpdate.calculatorType === "PA" ? "Payment Amount" : "Loan Amount"}`,
    text: previewUpdate.calculatorType === "PA" ? previewUpdate.paymentAmount : previewUpdate.loanAmount
  }, {
    label: 'Submitting User First Name',
    text: suUpdate.firstName !== suCurrent.firstName ? <span style={styles.update}>{suUpdate.firstName ?? ''}</span> : (suUpdate.firstName ?? '')
  }, {
    label: 'Submitting User Last Name',
    text: suUpdate.lastName !== suCurrent.lastName ? <span style={styles.update}>{suUpdate.lastName ?? ''}</span> : (suUpdate.lastName ?? '')
  }, {
    label: 'Submitting User Email',
    text: suUpdate.email !== suCurrent.email ? <span style={styles.update}>{suUpdate.email ?? ''}</span> : (suUpdate.email ?? '')
  }, {
    label: 'Submitting User Phone',
    text: suUpdate.phone !== suCurrent.phone ? <span style={styles.update}>{suUpdate.phone ?? ''}</span> : (suUpdate.phone ?? '')
  }, {
    label: 'Use Connected Orgs',
    text: connectedOrgFlagChanged ? (
      <span style={styles.update}>{fmtUseConnectedOrgs(ucUpdate)}</span>
    ) : fmtUseConnectedOrgs(ucUpdate)
  }, ...(showConnectedOrgIds ? [{
    label: 'Parent Organization Client Id',
    text: parentOrgClientIdChanged ? (
      <span style={styles.update}>{pocUpdate || '—'}</span>
    ) : (pocUpdate || '—')
  }, {
    label: 'Organization Id',
    text: organizationIdChanged ? (
      <span style={styles.update}>{oidUpdate || '—'}</span>
    ) : (oidUpdate || '—')
  }] : [])] : [];

  return (
      <Modal variant="full" isOpen={isOpen} title="Preview QuickLink Update" onDismiss={() => toggleOpen(false)}>
        <Box
          sx={{
            maxHeight: "calc(100vh - 180px)",
            overflowY: "auto",
            overflowX: "hidden",
            pr: 1,
            pb: 4,
          }}
        >
        <Grid
          templateColumns="1fr 1fr"
          templateRows="auto"
          gap="0.5rem"
        >
          {previewCurrent && (
          <View backgroundColor="#d7d7d7" padding="1rem">
            <Flex
              direction="column"
              alignItems="flex-start"
              gap="0.5rem"
            >
              <Heading level={6}>
                Current
              </Heading>
              <DataList columns={[1]} items={currentDataItems} />
            </Flex>
          </View>)}
          {previewUpdate && (
          <View backgroundColor="#d7d7d7" padding="1rem">
            <Flex
              direction="column"
              alignItems="flex-start"
              gap="0.5rem"
            >
              <Heading level={6}>
                Update
              </Heading>
              <DataList columns={[1]} items={updateDataItems} />
            </Flex>
          </View>)}
        </Grid>

        {showPaymentsSection ? (
        <Box
          sx={{
            mt: 3,
            mb: 2,
            border: "1px solid #cfd4d8",
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: "#fff",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#e8e8e8",
              borderBottom: "1px solid #cfd4d8",
              px: 3,
              py: 2,
            }}
          >
            <LumosHeading variant="h5" sx={{ m: 0 }}>
              Payments
            </LumosHeading>
            <Box sx={{ mt: 1, fontSize: 1, color: "#4a5560" }}>
              Create Payment Link options
              {paymentsChanged ? (
                <Box as="span" sx={{ ml: 2, color: "#c00", fontWeight: 600 }}>
                  (changed)
                </Box>
              ) : null}
            </Box>
          </Box>
          <Grid
            templateColumns="1fr 1fr"
            gap="0.5rem"
            style={{ padding: "1rem" }}
          >
            <View backgroundColor="#f7f8f9" padding="1rem">
              <Heading level={6}>Current</Heading>
              <Box sx={{ mt: 2, width: "100%" }}>
                <DataList
                  columns={[1]}
                  items={paymentLinkOptionsItems(currentPaymentsShareMethods)}
                />
              </Box>
            </View>
            <View backgroundColor="#f7f8f9" padding="1rem">
              <Heading level={6}>Update</Heading>
              <Box sx={{ mt: 2, width: "100%" }}>
                <DataList
                  columns={[1]}
                  items={paymentLinkOptionsItems(
                    updatePaymentsShareMethods,
                    currentPaymentsShareMethods
                  )}
                />
              </Box>
            </View>
          </Grid>
        </Box>
        ) : null}
        </Box>

        <ModalControls actions={[
            {
              onClick: () => updateApp(),
              label: 'Update',
            },
            {
              onClick: () => toggleOpen(false), label: 'Cancel'
            }
          ]}
        />
      </Modal>
  );
}

const styles = {
  update: {
    color: "#c00"
  }
}
export default UpdatePreview;
